/**
 * Sandbox · lista de corridas (v2.1).
 *
 * Layout dentro del Shell — warm dark/light cream, rounded-xl cards,
 * magenta puntual. Filtros chip rounded-full por status. Grid de runs
 * con KPIs en Plex Mono tabular-nums.
 */
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Check, Play, Plus, X } from "lucide-react";
import { useSandboxRuns } from "@/hooks/use-sandbox-run";
import { cn } from "@/lib/cn";
import type { SandboxRun } from "@/types";

type StatusFilter = "all" | "done" | "running" | "failed";

const FILTERS: Array<{ value: StatusFilter; label: string }> = [
  { value: "all", label: "Todas" },
  { value: "done", label: "Done" },
  { value: "running", label: "Running" },
  { value: "failed", label: "Failed" },
];

export function SandboxListPage() {
  const { data: runs, isLoading } = useSandboxRuns();
  const [filter, setFilter] = useState<StatusFilter>("all");

  const filtered = useMemo(() => {
    if (!runs) return [];
    if (filter === "all") return runs;
    return runs.filter((r) => r.status === filter);
  }, [runs, filter]);

  const counts = useMemo(() => {
    const base: Record<StatusFilter, number> = {
      all: runs?.length ?? 0,
      done: 0,
      running: 0,
      failed: 0,
    };
    runs?.forEach((r) => {
      if (r.status === "done") base.done++;
      else if (r.status === "running") base.running++;
      else if (r.status === "failed") base.failed++;
    });
    return base;
  }, [runs]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="px-8 py-6 flex items-start justify-between gap-4 border-b border-border-subtle">
        <div>
          <h1 className="font-sans font-semibold text-3xl tracking-snug text-text-primary">
            Sandbox
          </h1>
          <p className="text-sm text-text-muted mt-1">
            Backtesting de skills con preguntas masivas
          </p>
        </div>
        <Link to="/sandbox/new">
          <button
            className={cn(
              "inline-flex items-center gap-2 h-9 px-4 rounded-lg",
              "bg-magenta text-white text-sm font-medium",
              "hover:bg-magenta-hover transition-colors",
              "shadow-sm",
            )}
          >
            <Plus size={14} strokeWidth={2.25} />
            Nueva corrida
          </button>
        </Link>
      </header>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        {/* Filtros */}
        <div className="flex items-center gap-2 mb-6">
          {FILTERS.map((f) => {
            const active = filter === f.value;
            return (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={cn(
                  "inline-flex items-center gap-2 h-8 px-3 rounded-full text-xs font-medium",
                  "border transition-colors",
                  active
                    ? "bg-magenta-soft border-magenta-border text-magenta"
                    : "bg-surface-1 border-border text-text-secondary hover:border-border-strong hover:text-text-primary",
                )}
              >
                <span>{f.label}</span>
                <span
                  className={cn(
                    "font-mono tabular-nums text-2xs",
                    active ? "text-magenta" : "text-text-muted",
                  )}
                >
                  {counts[f.value]}
                </span>
              </button>
            );
          })}
        </div>

        {/* Grid de runs */}
        {isLoading ? (
          <div className="text-sm text-text-muted">Cargando…</div>
        ) : !filtered.length ? (
          <EmptyRuns hasAny={!!runs?.length} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((run) => (
              <RunCard key={run.id} run={run} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyRuns({ hasAny }: { hasAny: boolean }) {
  return (
    <div
      className={cn(
        "rounded-xl border border-border bg-surface-1 shadow-sm",
        "px-8 py-16 flex flex-col items-center text-center gap-3",
      )}
    >
      <div className="w-12 h-12 rounded-xl bg-surface-2 border border-border flex items-center justify-center text-text-secondary">
        <Play size={20} strokeWidth={1.75} />
      </div>
      <div className="text-sm font-medium text-text-primary">
        {hasAny ? "Sin corridas en este filtro" : "Sin corridas todavía"}
      </div>
      <p className="text-xs text-text-muted max-w-sm">
        Cargá un test set con preguntas para validar tu skill antes de
        promoverla a producción.
      </p>
      {!hasAny && (
        <Link to="/sandbox/new" className="mt-2">
          <button
            className={cn(
              "inline-flex items-center gap-2 h-9 px-4 rounded-lg",
              "bg-magenta text-white text-sm font-medium",
              "hover:bg-magenta-hover transition-colors",
            )}
          >
            <Plus size={14} strokeWidth={2.25} />
            Nueva corrida
          </button>
        </Link>
      )}
    </div>
  );
}

function RunCard({ run }: { run: SandboxRun }) {
  const progress = Math.round(
    (run.processed / Math.max(run.totalQuestions, 1)) * 100,
  );
  const matchRate = run.status === "done" ? Math.round(94 - (run.id.length % 6)) : null;
  const duration = run.finishedAt
    ? formatDuration(
        new Date(run.finishedAt).getTime() - new Date(run.createdAt).getTime(),
      )
    : null;

  return (
    <Link to="/sandbox/$runId" params={{ runId: run.id }}>
      <article
        className={cn(
          "rounded-xl border border-border bg-surface-1 p-5",
          "shadow-sm hover:shadow-md hover:border-border-strong",
          "hover:-translate-y-0.5 transition-all duration-150",
        )}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0 flex-1">
            <div className="font-mono text-sm font-medium text-text-primary truncate">
              {run.skillVersion}
              {run.comparisonVersion && (
                <span className="text-text-muted"> vs {run.comparisonVersion}</span>
              )}
            </div>
            <div className="font-mono text-2xs tabular-nums text-text-muted mt-0.5">
              {formatTimeAgo(run.createdAt)}
            </div>
          </div>
          <StatusBadge status={run.status} />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-3 pt-3 border-t border-border-subtle">
          <Stat label="Total" value={run.totalQuestions} />
          <Stat label="Done" value={run.processed} />
          <Stat
            label="Match"
            value={matchRate !== null ? `${matchRate}%` : "—"}
            tone={
              matchRate === null
                ? "muted"
                : matchRate >= 90
                  ? "success"
                  : matchRate >= 75
                    ? "warning"
                    : "error"
            }
          />
        </div>

        {/* Progress bar — solo running */}
        {run.status === "running" && (
          <div className="mb-3">
            <div className="h-1 rounded-full bg-surface-3 overflow-hidden">
              <div
                className="h-1 rounded-full bg-magenta transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="font-mono text-2xs tabular-nums text-text-muted mt-1.5">
              {run.processed}/{run.totalQuestions} · {progress}%
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 text-2xs text-text-muted">
          <span className="truncate">por {run.createdBy}</span>
          {duration && (
            <span className="font-mono tabular-nums flex-shrink-0">
              {duration}
            </span>
          )}
        </div>
      </article>
    </Link>
  );
}

function Stat({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number | string;
  tone?: "default" | "success" | "warning" | "error" | "muted";
}) {
  const toneClass = {
    default: "text-text-primary",
    success: "text-success-text",
    warning: "text-warning-text",
    error: "text-error-text",
    muted: "text-text-muted",
  }[tone];

  return (
    <div className="min-w-0">
      <div className="text-2xs uppercase tracking-wider text-text-muted mb-0.5">
        {label}
      </div>
      <div
        className={cn(
          "font-mono font-bold tabular-nums text-base",
          toneClass,
        )}
      >
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: SandboxRun["status"] }) {
  const config = {
    done: {
      cls: "bg-success-bg border-success-border text-success-text",
      label: "Done",
      icon: <Check size={9} strokeWidth={3} />,
    },
    failed: {
      cls: "bg-error-bg border-error-border text-error-text",
      label: "Failed",
      icon: <X size={9} strokeWidth={3} />,
    },
    running: {
      cls: "bg-info-bg border-info-border text-info-text",
      label: "Running",
      icon: (
        <span
          className="w-1.5 h-1.5 rounded-full bg-current animate-pulse-soft"
          aria-hidden
        />
      ),
    },
    queued: {
      cls: "bg-state-closed-bg border-state-closed-border text-state-closed-text",
      label: "Queued",
      icon: null,
    },
  }[status];

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full",
        "border text-2xs font-medium",
        config.cls,
      )}
    >
      {config.icon}
      {config.label}
    </span>
  );
}

function formatTimeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "hace un instante";
  if (mins < 60) return `hace ${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `hace ${hours}h`;
  const days = Math.floor(hours / 24);
  return `hace ${days}d`;
}

function formatDuration(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}
